import { SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Input,
  type InputRef,
  Modal,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Empty from '@/components/QuietEmpty';
import { useContactDetailDrawer } from '@/modules/zy-crm/components/contact-detail-drawer';
import { useCustomerDetailDrawer } from '@/modules/zy-crm/components/customer-detail-drawer';
import { useLeadDetailDrawer } from '@/modules/zy-crm/components/lead-detail-drawer';
import { useOpportunityDetailDrawer } from '@/modules/zy-crm/components/opportunity-detail-drawer';
import { useMemberDirectory } from '@/modules/zy-crm/member-directory';
import {
  type CrmGlobalSearchGroup,
  type CrmGlobalSearchItem,
  type CrmGlobalSearchType,
  queryGlobalSearch,
} from '@/services/bff';
import styles from './index.module.less';

const DEFAULT_LIMIT = 10;
const GROUP_ORDER: CrmGlobalSearchType[] = [
  'lead',
  'customer',
  'contact',
  'opportunity',
];
const GROUP_LABELS: Record<CrmGlobalSearchType, string> = {
  lead: '线索',
  customer: '客户',
  contact: '联系人',
  opportunity: '商机',
};
const buildEmptyGroup = (): CrmGlobalSearchGroup => ({
  list: [],
  total: 0,
  hasMore: false,
  nextOffset: 0,
});

const buildEmptyGroups = (): Record<
  CrmGlobalSearchType,
  CrmGlobalSearchGroup
> => ({
  lead: buildEmptyGroup(),
  customer: buildEmptyGroup(),
  contact: buildEmptyGroup(),
  opportunity: buildEmptyGroup(),
});

const normalizeGroups = (
  value?: Partial<Record<CrmGlobalSearchType, CrmGlobalSearchGroup>>,
): Record<CrmGlobalSearchType, CrmGlobalSearchGroup> => {
  const emptyGroups = buildEmptyGroups();
  if (!value) {
    return emptyGroups;
  }

  return GROUP_ORDER.reduce(
    (acc, type) => {
      const group = value[type];
      acc[type] = {
        ...emptyGroups[type],
        ...(group || {}),
        list: group?.list || [],
      };
      return acc;
    },
    { ...emptyGroups },
  );
};

const normalizeText = (value?: string | number | null) => {
  const text = String(value || '').trim();
  return text || '-';
};

const renderResultMeta = (
  item: CrmGlobalSearchItem,
  memberNameMap: Record<string, string>,
) => {
  const ownerName = item.ownerUserUuid
    ? memberNameMap[item.ownerUserUuid] || '-'
    : '-';
  return [`负责人 ${ownerName}`];
};

export function CrmGlobalSearch() {
  const { openContact } = useContactDetailDrawer();
  const { openCustomer } = useCustomerDetailDrawer();
  const { memberNameMap } = useMemberDirectory();
  const { openLead } = useLeadDetailDrawer();
  const { openOpportunity } = useOpportunityDetailDrawer();
  const inputRef = useRef<InputRef>(null);
  const latestRequestIdRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [groups, setGroups] = useState(buildEmptyGroups);
  const [activeType, setActiveType] = useState<CrmGlobalSearchType>('lead');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState<
    Record<CrmGlobalSearchType, boolean>
  >({
    lead: false,
    customer: false,
    contact: false,
    opportunity: false,
  });

  const resetSearchState = () => {
    setKeyword('');
    setDebouncedKeyword('');
    setGroups(buildEmptyGroups());
    setActiveType('lead');
    setLoading(false);
    setLoadingMore({
      lead: false,
      customer: false,
      contact: false,
      opportunity: false,
    });
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedKeyword(keyword.trim());
    }, 250);
    return () => window.clearTimeout(timer);
  }, [keyword]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!debouncedKeyword) {
      setGroups(buildEmptyGroups());
      setLoading(false);
      return;
    }

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    setLoading(true);

    void queryGlobalSearch({
      keyword: debouncedKeyword,
      limit: DEFAULT_LIMIT,
    })
      .then((response) => {
        if (latestRequestIdRef.current !== requestId) {
          return;
        }
        const nextGroups = normalizeGroups(response.groups);
        setGroups(nextGroups);
        setActiveType(
          GROUP_ORDER.find((type) => nextGroups[type].list.length > 0) ||
            'lead',
        );
      })
      .finally(() => {
        if (latestRequestIdRef.current === requestId) {
          setLoading(false);
        }
      });
  }, [debouncedKeyword, open]);

  const hasAnyResult = useMemo(
    () => GROUP_ORDER.some((type) => (groups[type]?.list || []).length > 0),
    [groups],
  );
  const activeGroup = groups[activeType] || buildEmptyGroup();

  const handleOpenDetail = (item: CrmGlobalSearchItem) => {
    if (!item.canViewDetail) {
      return;
    }
    setOpen(false);
    resetSearchState();
    if (item.type === 'customer') {
      openCustomer({
        customerUuid: item.uuid,
        customerName: item.name,
      });
      return;
    }
    if (item.type === 'contact') {
      openContact({
        contactUuid: item.uuid,
        contactName: item.name,
      });
      return;
    }
    if (item.type === 'lead') {
      openLead({
        leadUuid: item.uuid,
        leadName: item.name,
      });
      return;
    }
    openOpportunity({
      opportunityUuid: item.uuid,
      opportunityName: item.name,
    });
  };

  const handleLoadMore = async (type: CrmGlobalSearchType) => {
    if (!debouncedKeyword || loadingMore[type] || !groups[type]?.hasMore) {
      return;
    }
    setLoadingMore((prev: Record<CrmGlobalSearchType, boolean>) => ({
      ...prev,
      [type]: true,
    }));
    try {
      const response = await queryGlobalSearch({
        keyword: debouncedKeyword,
        limit: DEFAULT_LIMIT,
        types: [type],
        offsetByType: {
          [type]: groups[type]?.nextOffset || 0,
        },
      });
      const nextGroup = response.groups?.[type];
      if (!nextGroup) {
        return;
      }
      setGroups((prev: Record<CrmGlobalSearchType, CrmGlobalSearchGroup>) => ({
        ...prev,
        [type]: {
          ...nextGroup,
          list: [...(prev[type]?.list || []), ...nextGroup.list],
        },
      }));
    } finally {
      setLoadingMore((prev: Record<CrmGlobalSearchType, boolean>) => ({
        ...prev,
        [type]: false,
      }));
    }
  };

  const handleClose = () => {
    setOpen(false);
    resetSearchState();
  };

  return (
    <>
      <SearchOutlined
        className={styles.trigger}
        aria-label="打开全局搜索"
        title="打开全局搜索"
        onClick={() => setOpen(true)}
      />
      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        open={open}
        centered
        width={1080}
        footer={null}
        destroyOnHidden
        onCancel={handleClose}
        className={styles.modal}
      >
        <div className={styles.panel}>
          <header className={styles.header}>
            <Space className={styles.searchRow} size={12} align="start">
              <Input
                ref={inputRef}
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索线索、客户、联系人、商机"
                prefix={<SearchOutlined />}
                allowClear
                className={styles.searchInput}
              />
            </Space>
          </header>

          <div className={styles.groupsShell}>
            {loading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : !debouncedKeyword ? (
              <div className={styles.placeholder}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="输入关键词开始搜索"
                />
              </div>
            ) : !hasAnyResult ? (
              <div className={styles.placeholder}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={false}
                />
              </div>
            ) : (
              <div className={styles.groupBoard}>
                <aside className={styles.typeRail} aria-label="分类列表">
                  <Typography.Text className={styles.railLabel}>
                    分类
                  </Typography.Text>
                  {GROUP_ORDER.map((type) => {
                    const group = groups[type];
                    const isActive = activeType === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        className={`${styles.typeItem} ${isActive ? styles.typeItemActive : ''}`}
                        onClick={() => setActiveType(type)}
                      >
                        <span className={styles.typeName}>
                          {GROUP_LABELS[type]}
                        </span>
                        <Tag className={styles.typeCount}>
                          {group.list.length}/{group.total}
                        </Tag>
                      </button>
                    );
                  })}
                </aside>
                <section className={styles.typePane}>
                  <div className={styles.contentHead}>
                    <div>
                      <Typography.Title
                        level={4}
                        className={styles.contentTitle}
                      >
                        {GROUP_LABELS[activeType]}
                      </Typography.Title>
                    </div>
                    <Tag className={styles.resultBadge}>
                      共 {activeGroup.total} 条结果
                    </Tag>
                  </div>

                  <div className={styles.resultList}>
                    {activeGroup.list.map((item) => {
                      const metas = renderResultMeta(item, memberNameMap);
                      return (
                        <Tooltip
                          key={item.uuid}
                          title={
                            item.canViewDetail
                              ? undefined
                              : '当前记录无详情权限'
                          }
                        >
                          <Card
                            hoverable={item.canViewDetail}
                            className={`${styles.resultCard} ${
                              item.canViewDetail
                                ? ''
                                : styles.resultCardDisabled
                            }`}
                            onClick={
                              item.canViewDetail
                                ? () => handleOpenDetail(item)
                                : undefined
                            }
                            styles={{ body: { padding: 0 } }}
                          >
                            <div className={styles.resultCardInner}>
                              <div className={styles.resultMain}>
                                <div className={styles.resultTitle}>
                                  <Typography.Text
                                    className={styles.resultName}
                                    ellipsis
                                  >
                                    {normalizeText(item.name)}
                                  </Typography.Text>
                                  {!item.canViewDetail ? (
                                    <Tag className={styles.resultStatus}>
                                      无详情权限
                                    </Tag>
                                  ) : null}
                                </div>
                                <Space
                                  size={[8, 8]}
                                  wrap
                                  className={styles.resultMeta}
                                >
                                  {metas.map((meta) => (
                                    <Tag key={meta} className={styles.metaTag}>
                                      {meta}
                                    </Tag>
                                  ))}
                                </Space>
                              </div>
                            </div>
                          </Card>
                        </Tooltip>
                      );
                    })}
                  </div>

                  {activeGroup.hasMore ? (
                    <Button
                      type="link"
                      className={styles.loadMore}
                      loading={loadingMore[activeType]}
                      onClick={() => void handleLoadMore(activeType)}
                    >
                      查看更多
                    </Button>
                  ) : null}
                </section>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}

export default CrmGlobalSearch;
